import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { publicApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const { refresh, user } = useAuth();
    const [state, setState] = useState("checking");
    const [errorMsg, setErrorMsg] = useState("");
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        if (!token) { setState("error"); setErrorMsg("No token in the URL"); return; }
        publicApi.post("/auth/verify-email", { token })
            .then(() => { refresh(); setState("ok"); })
            .catch((err) => {
                setErrorMsg(err?.response?.data?.detail || "Verification failed");
                setState("error");
            });
    }, [token, refresh]);

    return (
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
            <div className="max-w-md w-full border border-white/10 p-10 bg-[#0a0a0a]" data-testid="verify-root">
                {state === "checking" && (
                    <>
                        <Loader2 className="w-8 h-8 animate-spin text-white" strokeWidth={1.5} />
                        <h1 className="mt-6 font-display font-black text-3xl tracking-tighter">Verifying…</h1>
                    </>
                )}
                {state === "ok" && (
                    <>
                        <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={1.5} />
                        <div className="mt-6 font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500">// VERIFIED</div>
                        <h1 className="mt-2 font-display font-black text-3xl tracking-tighter">You're all set.</h1>
                        <p className="mt-3 text-zinc-400 text-sm">Your email is confirmed. {user ? "You're logged in — jump into the app." : "Sign in to start repurposing."}</p>
                        <Link to={user ? "/app/dashboard" : "/login"}>
                            <Button data-testid="verify-cta" className="mt-8 w-full bg-white text-black hover:bg-zinc-200 h-11 font-semibold">
                                {user ? "Go to dashboard" : "Sign in"} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </>
                )}
                {state === "error" && (
                    <>
                        <XCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
                        <h1 className="mt-6 font-display font-black text-3xl tracking-tighter">Verification failed</h1>
                        <p className="mt-3 text-zinc-400 text-sm">{errorMsg}. Sign in and request a new verification email from your dashboard.</p>
                        <Link to="/login">
                            <Button variant="outline" data-testid="verify-error-cta" className="mt-8 w-full bg-transparent border-white/15 hover:bg-white/5 text-white h-11">Back to login</Button>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
